import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { AppConfig } from '../config/config';
import { ToastrService } from 'ngx-toastr';
import { PermissionsEnum } from '../models/role.model';
import {
  DashboardConfiguration,
  UtilityLanguageSuffix,
} from '../models/utility.model';
import { PermissionsService } from 'shared-lib';
import { ReportStateService } from '../components/reports/services/report-state.service';
import { Language } from '../models/user.model';
import { ResponsePackage } from '../models/util.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private pathApi = this.config.setting['pathApi'] + 'auth/';
  private jwtService = new JwtHelperService();
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private http: HttpClient,
    private config: AppConfig,
    private router: Router,
    private toastr: ToastrService,
    public permissionsService: PermissionsService,
    private reportStateService: ReportStateService
  ) {}

  login(email: string, password: string): Observable<any> {
    return this.http.post(this.pathApi + 'login', {
      Email: email,
      Password: password,
    });
  }

  tokenLogin(token: string): Observable<any> {
    return this.http.post(this.pathApi + 'tokenLogin', { Token: token });
  }

  bpsLogin(email: string, pass: string, utilityId: string): Observable<any> {
    return this.http.post(this.pathApi + 'bpsLogin', {
      Email: email,
      Password: pass,
      UtilityId: parseInt(utilityId),
    });
  }

  bpsLoginFirstStep(email: string, utilityId: number): Observable<any> {
    return this.http.post(this.pathApi + 'bpsLoginFirstStep', {
      Email: email,
      UtilityId: utilityId,
    });
  }

  logout(routeAfterClear: boolean) {
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('colorCode');
    localStorage.removeItem('configuration');
    localStorage.removeItem('defaultCaimsCodePatterns');
    localStorage.removeItem('imageUrl');
    localStorage.removeItem('filter');
    localStorage.removeItem('filterMeter');
    localStorage.removeItem('filterPole');
    localStorage.removeItem('filterTransformer');
    localStorage.removeItem('filterCustomer');
    localStorage.removeItem('filterBuilding');
    localStorage.removeItem('filterCustomer');
    localStorage.removeItem('filterBuilding');
    localStorage.removeItem('filterMvPole');
    localStorage.removeItem('filterPaymentHistory');
    localStorage.removeItem('filterServiceRequestOnboardedCustomers');
    localStorage.removeItem('filterServiceRequestAuthorization');
    localStorage.removeItem('filterSiteVerification');
    localStorage.removeItem('installationsMeterTab');
    localStorage.removeItem('cameFrom');
    localStorage.removeItem('notVisibleFields');
    localStorage.removeItem('currency');
    localStorage.removeItem('preferredLanguage');
    localStorage.removeItem('languageSuffixes');
    localStorage.removeItem('mapConfiguration');
    this.reportStateService.clearAllReportFilters({
      clearMainTabSelection: true,
      clearDataQualityTabsSelection: true,
    });

    window.sessionStorage.clear();
    this.permissionsService.resetPermissions();
    if (routeAfterClear) {
      this.router.navigate(['']);
    }
  }

  updateUserInLocalStorageNoApi(user: any) {
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  private isWellFormedJwt(token: string | null): token is string {
    return !!token && token.split('.').length === 3;
  }

  isLoggedIn() {
    let token = localStorage.getItem('token');
    if (!this.isWellFormedJwt(token)) return false;
    if (this.jwtService.isTokenExpired(token)) {
      this.logout(true);
      this.toastr.error('Session timed out.');
    }
    return !this.jwtService.isTokenExpired(token);
  }

  public getCurrentUser() {
    let token = localStorage.getItem('token');
    if (!this.isWellFormedJwt(token)) return null;

    return this.jwtService.decodeToken(token);
  }

  public isAuthorized(permission: PermissionsEnum) {
    let rolePermissions = localStorage.getItem('permissions');

    if (!rolePermissions)
      // dont have permissions on local storage
      return false;

    let permissions = rolePermissions ? JSON.parse(rolePermissions) : [];
    let filteredPerms = permissions.filter((x: any) => x == permission);

    if (filteredPerms) return filteredPerms[0];
    return null;
  }

  public hasConfiguration(config: any) {
    let configuration = localStorage.getItem('configuration');
    if (!configuration) return false;

    let configurations = configuration ? JSON.parse(configuration) : [];
    return configurations.includes(config);
  }

  get hasCimDataIntegration(): boolean {
    return this.hasConfiguration(DashboardConfiguration.CIMDataIntegration);
  }

  public hasUtility() {
    let token = localStorage.getItem('token');
    if (!this.isWellFormedJwt(token)) return null;

    let currentUser = this.jwtService.decodeToken(token);
    return currentUser.utilityId != '';
  }

  public decodeJWTToken() {
    var token = localStorage.getItem('token');
    if (!this.isWellFormedJwt(token)) return null;
    const helper = new JwtHelperService();
    var decoded = helper.decodeToken(token);
    return decoded;
  }

  sendEmail(obj: any): Observable<any> {
    return this.http.post(`${this.pathApi}sendMessage`, obj);
  }

  changePreferredLanguage(language: Language) {
    return this.http.post<ResponsePackage<UtilityLanguageSuffix[]>>(
      `${this.pathApi}changePreferredLanguage`,
      language
    );
  }
  getSas(containerName?: string): Observable<any> {
    return this.http.get(`${this.pathApi}sasToken`, {
      headers: this.headers,
      params: containerName ? { containerName } : {},
      observe: 'response',
    });
  }
}
